import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Header,
  SpaceBetween,
  Button,
  Form,
  FormField,
  Input,
  Alert,
  Spinner,
  BreadcrumbGroup,
  ColumnLayout,
  Box,
  Tabs,
  Select,
  Toggle,
} from "@cloudscape-design/components";
import { useAuth } from "../AuthContext";
import { useNotifications } from "./NotificationContext";
import { LOCAL_API } from "../config/api";
import AppLayoutWrapper from "./AppLayoutWrapper";

const defaultEmailConfig = {
  type: "google",
  email: "",
  notification_emails: "",
  isGoogleAuthorized: false,
  smtp: { server: "", port: 587, useTLS: true },
  imap: { server: "", port: 993, useSSL: true },
};

const CompanySettings = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { success: notifySuccess, error: notifyError } = useNotifications();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [companyInfo, setCompanyInfo] = useState({ name: "", address: "" });
  const [contactInfo, setContactInfo] = useState({ name: "", email: "", mobile: "" });
  const [emailConfig, setEmailConfig] = useState(defaultEmailConfig);

  const accessToken = user?.access_token || user?.token;

  const fetchCompany = useCallback(async () => {
    if (!accessToken) return;
    try {
      setLoading(true);
      setError(null);
      const res = await fetch(LOCAL_API.companyProfile, {
        headers: { Authorization: `Bearer ${accessToken}` },
      });
      const data = await res.json();
      if (res.ok && data.status === "success") {
        const d = data.data;
        setCompany(d);
        setCompanyInfo(d.companyInfo || { name: "", address: "" });
        setContactInfo(d.contactInfo || { name: "", email: "", mobile: "" });
        setEmailConfig({
          ...defaultEmailConfig,
          ...(d.emailConfig || {}),
          smtp: { ...defaultEmailConfig.smtp, ...(d.emailConfig?.smtp || {}) },
          imap: { ...defaultEmailConfig.imap, ...(d.emailConfig?.imap || {}) },
        });
      } else {
        setError(data.message || "Impossible de charger les informations de la company.");
      }
    } catch (err) {
      setError("Erreur réseau lors du chargement du profil company.");
    } finally {
      setLoading(false);
    }
  }, [accessToken]);

  useEffect(() => {
    fetchCompany();
  }, [fetchCompany]);

  const handleSave = async () => {
    if (!accessToken) return;
    const companyName = (companyInfo.name || "").trim();
    const contactName = (contactInfo.name || "").trim();
    const contactEmail = (contactInfo.email || "").trim();
    if (!companyName) {
      notifyError("Le nom de la company est obligatoire.");
      return;
    }
    if (!contactName) {
      notifyError("Le nom du contact est obligatoire.");
      return;
    }
    if (!contactEmail) {
      notifyError("L'email du contact est obligatoire.");
      return;
    }
    try {
      setSaving(true);
      const res = await fetch(LOCAL_API.companyProfile, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({
          companyInfo,
          contactInfo,
          emailConfig: {
            ...emailConfig,
            email: contactEmail,
          },
        }),
      });
      const data = await res.json();
      if (res.ok) {
        notifySuccess("Company mise à jour avec succès.");
        setEditMode(false);
        await fetchCompany();
      } else {
        notifyError(data.message || "Erreur lors de la mise à jour de la company.");
      }
    } catch (err) {
      notifyError("Erreur réseau lors de la sauvegarde.");
    } finally {
      setSaving(false);
    }
  };

  const emailTypeOptions = [
    { label: "Google", value: "google" },
    { label: "Regular Email (SMTP/IMAP)", value: "regular" },
  ];

  const details = company
    ? [
        { label: "Company name", value: companyInfo.name || "N/A" },
        { label: "Address", value: companyInfo.address || "N/A" },
        { label: "Contact name", value: contactInfo.name || "N/A" },
        { label: "Contact email", value: contactInfo.email || "N/A" },
        { label: "Contact mobile", value: contactInfo.mobile || "N/A" },
        { label: "Email type", value: emailConfig.type === "google" ? "Google" : "Regular Email" },
      ]
    : [];

  return (
    <AppLayoutWrapper
      breadcrumbs={
        <BreadcrumbGroup
          items={[
            { text: "Home", href: "/dashboard" },
            { text: "Company Settings", href: "/settings/company" },
          ]}
          onFollow={(event) => {
            event.preventDefault();
            navigate(event.detail.href);
          }}
        />
      }
      header={{
        title: "Company Settings",
        description: "Configure your company information once. This is used for all agents and billing.",
      }}
      headerBackgroundStyle="linear-gradient(to bottom, rgba(9, 29, 43, 1) 2%, rgba(0, 108, 224, 1) 8%, rgba(255, 255, 255, 1) 82%)"
    >
      <SpaceBetween size="l">
        {error && <Alert type="error">{error}</Alert>}
        {loading && <Box textAlign="center" padding={{ top: "xxxl" }}><Spinner size="large" /></Box>}
        {!loading && company && (
          <Tabs
            activeTabId={editMode ? "edit" : "details"}
            onChange={({ detail }) => setEditMode(detail.activeTabId === "edit")}
            tabs={[
              {
                id: "details",
                label: "Company details",
                content: (
                  <Container header={<Header variant="h2" actions={<Button onClick={() => setEditMode(true)}>Edit</Button>}>Company details</Header>}>
                    <ColumnLayout columns={{ default: 1, s: 2 }} variant="text-grid">
                      {details.map((item, idx) => (
                        <div key={idx}>
                          <Box variant="awsui-key-label">{item.label}</Box>
                          <Box variant="span">{item.value}</Box>
                        </div>
                      ))}
                    </ColumnLayout>
                  </Container>
                ),
              },
              {
                id: "edit",
                label: "Edit company",
                content: (
                  <Container header={<Header variant="h2">Edit company</Header>}>
                    <Form
                      actions={
                        <SpaceBetween direction="horizontal" size="xs">
                          <Button variant="link" onClick={() => setEditMode(false)} disabled={saving}>
                            Cancel
                          </Button>
                          <Button variant="primary" onClick={handleSave} loading={saving}>
                            Save changes
                          </Button>
                        </SpaceBetween>
                      }
                    >
                      <SpaceBetween size="l">
                        <Container header={<Header variant="h3">Company information</Header>}>
                          <ColumnLayout columns={{ default: 1, s: 2 }}>
                            <FormField label="Company name" constraintText="Required">
                              <Input
                                value={companyInfo.name}
                                onChange={(e) => setCompanyInfo((p) => ({ ...p, name: e.detail.value }))}
                              />
                            </FormField>
                            <FormField label="Address">
                              <Input
                                value={companyInfo.address}
                                onChange={(e) => setCompanyInfo((p) => ({ ...p, address: e.detail.value }))}
                              />
                            </FormField>
                          </ColumnLayout>
                        </Container>
                        <Container header={<Header variant="h3">Contact information</Header>}>
                          <ColumnLayout columns={{ default: 1, s: 2 }}>
                            <FormField label="Contact name" constraintText="Required">
                              <Input
                                value={contactInfo.name}
                                onChange={(e) => setContactInfo((p) => ({ ...p, name: e.detail.value }))}
                              />
                            </FormField>
                            <FormField label="Contact email" constraintText="Required">
                              <Input
                                value={contactInfo.email}
                                onChange={(e) => setContactInfo((p) => ({ ...p, email: e.detail.value }))}
                              />
                            </FormField>
                            <FormField label="Contact mobile">
                              <Input
                                value={contactInfo.mobile}
                                onChange={(e) => setContactInfo((p) => ({ ...p, mobile: e.detail.value }))}
                              />
                            </FormField>
                          </ColumnLayout>
                        </Container>
                        <Container header={<Header variant="h3">Email configuration</Header>}>
                          <ColumnLayout columns={{ default: 1, s: 2 }}>
                            <FormField label="Email type">
                              <Select
                                selectedOption={emailTypeOptions.find((o) => o.value === emailConfig.type)}
                                onChange={({ detail }) => setEmailConfig((p) => ({ ...p, type: detail.selectedOption.value }))}
                                options={emailTypeOptions}
                              />
                            </FormField>
                            {emailConfig.type === "regular" && (
                              <>
                                <FormField label="Email password">
                                  <Input
                                    type="password"
                                    value={emailConfig.password || ""}
                                    onChange={(e) => setEmailConfig((p) => ({ ...p, password: e.detail.value }))}
                                  />
                                </FormField>
                                <FormField label="SMTP server">
                                  <Input
                                    value={emailConfig.smtp.server}
                                    onChange={(e) => setEmailConfig((p) => ({ ...p, smtp: { ...p.smtp, server: e.detail.value } }))}
                                  />
                                </FormField>
                                <FormField label="SMTP port">
                                  <Input
                                    type="number"
                                    value={String(emailConfig.smtp.port)}
                                    onChange={(e) => setEmailConfig((p) => ({ ...p, smtp: { ...p.smtp, port: Number(e.detail.value) } }))}
                                  />
                                </FormField>
                                <FormField label="SMTP TLS">
                                  <Toggle
                                    checked={!!emailConfig.smtp.useTLS}
                                    onChange={({ detail }) => setEmailConfig((p) => ({ ...p, smtp: { ...p.smtp, useTLS: detail.checked } }))}
                                  >
                                    Use TLS
                                  </Toggle>
                                </FormField>
                                <FormField label="IMAP server">
                                  <Input
                                    value={emailConfig.imap.server}
                                    onChange={(e) => setEmailConfig((p) => ({ ...p, imap: { ...p.imap, server: e.detail.value } }))}
                                  />
                                </FormField>
                                <FormField label="IMAP port">
                                  <Input
                                    type="number"
                                    value={String(emailConfig.imap.port)}
                                    onChange={(e) => setEmailConfig((p) => ({ ...p, imap: { ...p.imap, port: Number(e.detail.value) } }))}
                                  />
                                </FormField>
                                <FormField label="IMAP SSL">
                                  <Toggle
                                    checked={!!emailConfig.imap.useSSL}
                                    onChange={({ detail }) => setEmailConfig((p) => ({ ...p, imap: { ...p.imap, useSSL: detail.checked } }))}
                                  >
                                    Use SSL
                                  </Toggle>
                                </FormField>
                              </>
                            )}
                          </ColumnLayout>
                        </Container>
                      </SpaceBetween>
                    </Form>
                  </Container>
                ),
              },
            ]}
          />
        )}
      </SpaceBetween>
    </AppLayoutWrapper>
  );
};

export default CompanySettings;
